import React, { useEffect, useState } from 'react';
import {
	ProjectState,
	EntityWithValueState,
	ChangeSet,
	getParentCanvasEntity,
	parseA1NotationToSpreadsheetRange,
	parseSpreadsheetRangeToA1Notation,
	StateMutationAction,
	IValueStoreClient,
	DynamicValueTypes,
	LiteralValueState
} from '@elyx-code/project-logic-tree';
import { columnLetterToIndex } from '@elyx-code/project-logic-tree';
import { handleLiteralValueChange } from '../../services/execution/value-resolution';
import Button from '../../lib/button/Button';
import {
	Box,
	TextField,
	Typography,
	IconButton,
	InputAdornment,
	Tabs,
	Tab
} from '@mui/material';
import JsonEditorDialog from './JsonEditorDialog';
import { EditorService } from '../../services/editor';
import { popupNotification } from '../../popup-notification';

export interface ISpreadsheetRangeBuilderDialogProps {
	project: EditorService;
	valueOwner: EntityWithValueState;
	onlyDefault?: boolean;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	onClose?: (e: React.MouseEvent | null) => void;
}

const TABS = ['Visual', 'JSON'];

const SpreadsheetRangeBuilderDialog: React.FC<
	ISpreadsheetRangeBuilderDialogProps
> = (props) => {
	const onlyDefault = props.onlyDefault || false;
	const [activeTab, setActiveTab] = useState(0);
	let initialValue: any = props.value?.value ||
		props.value || {
			sheet: '',
			startColumn: '',
			startRow: '',
			endColumn: '',
			endRow: ''
		};
	const [rangeValue, setRangeValue] = useState<any>(initialValue);
	const [a1Notation, setA1Notation] = useState<string>('');
	const [validationError, setValidationError] = useState<string | null>(null);
	const [rangeMode, setRangeMode] = useState<'coordinates' | 'namedRange'>(
		initialValue.namedRange ? 'namedRange' : 'coordinates'
	);
	const [isA1Editing, setIsA1Editing] = useState(false);

	useEffect(() => {
		if (isA1Editing) return;
		try {
			const tempValue = { ...rangeValue };
			if (rangeMode === 'coordinates') {
				delete tempValue.namedRange;
			} else {
				delete tempValue.startColumn;
				delete tempValue.startRow;
				delete tempValue.endColumn;
				delete tempValue.endRow;
				delete tempValue.sheet;
			}

			setA1Notation(
				parseSpreadsheetRangeToA1Notation(tempValue, {
					errorPrefix: 'Range '
				}) || ''
			);
			setValidationError(null);
		} catch (e: any) {
			setValidationError(e.message || 'Invalid range configuration');
		}
	}, [rangeValue, rangeMode, isA1Editing]);

	const handleSave = () => {
		if (validationError && rangeMode === 'coordinates') return;

		let storage: ChangeSet | IValueStoreClient =
			props.project.localTestValues;

		if (onlyDefault) {
			storage = props.project.logic.addChangeSet(
				new ChangeSet(
					props.project.logic,
					ProjectState.sessionAuthor,
					new Date().toISOString(),
					props.valueOwner,
					false,
					StateMutationAction.SetPrimitiveDefaultValue
				)
			);
		}

		const valueToSave: any = { ...rangeValue };
		if (rangeMode === 'coordinates') {
			delete valueToSave.namedRange;
			if (valueToSave.sheet !== '') {
				valueToSave.sheet = Number(valueToSave.sheet);
				if (isNaN(valueToSave.sheet))
					valueToSave.sheet = rangeValue.sheet;
			}
		} else {
			delete valueToSave.startColumn;
			delete valueToSave.startRow;
			delete valueToSave.endColumn;
			delete valueToSave.endRow;
			delete valueToSave.sheet;
		}

		const actualChangeSet = handleLiteralValueChange(
			valueToSave,
			props.valueOwner,
			props.project.logic,
			storage,
			{
				forVarInst: true
			}
		);
		if (!!actualChangeSet) {
			props.project.renderAndCloseChangeSet(actualChangeSet);
		}

		const valueOwnerCanvasParentEntity = getParentCanvasEntity(
			props.valueOwner
		);
		if (valueOwnerCanvasParentEntity) {
			props.project.emit(valueOwnerCanvasParentEntity.id, {});
		}
		props.project.test?.validate();
		props.onClose?.(null);
	};

	const handleA1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const a1 = e.target.value;
		setA1Notation(a1);
		setRangeMode('coordinates');
		try {
			const parsed = parseA1NotationToSpreadsheetRange(a1);
			if (parsed) {
				setRangeValue({
					...rangeValue,
					...parsed
				});
			}
			setValidationError(null);
		} catch (err: any) {
			setValidationError(err.message || 'Invalid range configuration');
		}
	};

	const updateField = (field: string, value: string) => {
		setRangeValue((prev: any) => {
			const next = { ...prev, [field]: value };
			if (field === 'startRow') {
				const sR = getRowInt(value);
				const eR = getRowInt(prev.endRow);
				if (!isNaN(sR) && !isNaN(eR) && sR > eR) {
					next.endRow = value;
				}
			}
			if (field === 'startColumn') {
				const sC = getColInt(value);
				const eC = getColInt(prev.endColumn);
				if (!isNaN(sC) && !isNaN(eC) && sC > eC) {
					next.endColumn = value;
				}
			}
			return next;
		});
	};

	const getColInt = (c: any) => {
		if (typeof c === 'string') {
			const cleaned = c.replace('$', '').toUpperCase();
			if (/^\d+$/.test(cleaned)) return parseInt(cleaned, 10);
			return columnLetterToIndex(cleaned);
		}
		return c;
	};
	const getRowInt = (r: any) => {
		if (typeof r === 'string') {
			const cleaned = r.replace('$', '');
			if (!/^\d+$/.test(cleaned)) return NaN;
			return parseInt(cleaned, 10);
		}
		return r;
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				paddingTop: '40px'
			}}
		>
			<span style={{ padding: '0 40px' }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						className="dialog_back_button"
						onClick={(e: any) => props.onClose?.(e)}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px'
						}}
					>
						Spreadsheet Range Builder
					</h1>
				</div>
			</span>
			<Box flex={1} overflow="auto">
				<Box
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						padding: '0 40px'
					}}
				>
					<Tabs
						value={activeTab}
						onChange={(e, val) => setActiveTab(val)}
					>
						{TABS.map((tab, idx) => (
							<Tab key={idx} label={tab} />
						))}
					</Tabs>
				</Box>
				{activeTab === 0 && (
					<Box
						sx={{
							padding: '20px 40px',
							height: '100%',
							boxSizing: 'border-box'
						}}
					>
						<Box display="flex" flexDirection="column" gap={2}>
							<TextField
								label="A1 Notation"
								value={a1Notation}
								onChange={handleA1Change}
								onFocus={() => setIsA1Editing(true)}
								onBlur={() => setIsA1Editing(false)}
								placeholder="Sheet1!A1:B10"
								fullWidth
								size="small"
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => {
													navigator.clipboard.writeText(
														a1Notation
													);
													popupNotification({
														text: 'Copied to clipboard'
													});
												}}
											>
												<i className="fa-regular fa-copy" />
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
							<Box display="flex" gap={2} alignItems="stretch">
								<Box
									flex={1}
									display="flex"
									flexDirection="column"
									gap={2}
									position="relative"
									border="1px solid #e2e8f0"
									borderRadius={2}
									p={2}
								>
									{rangeMode !== 'coordinates' && (
										<Box
											position="absolute"
											top={0}
											left={0}
											right={0}
											bottom={0}
											bgcolor="rgba(255, 255, 255, 0.4)"
											sx={{
												backdropFilter: 'blur(2px)',
												cursor: 'pointer',
												zIndex: 10,
												borderRadius: 2,
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												'& .hover-text': {
													opacity: 0,
													transition: 'opacity 0.2s'
												},
												'&:hover .hover-text': {
													opacity: 1
												}
											}}
											onClick={() =>
												setRangeMode('coordinates')
											}
										>
											<Box
												className="hover-text"
												display="flex"
												flexDirection="column"
												alignItems="center"
												sx={{
													background:
														'rgba(255,255,255,0.8)',
													padding: 1,
													borderRadius: 1
												}}
											>
												<Typography
													variant="subtitle2"
													fontWeight="bold"
												>
													Coordinates
												</Typography>
												<Typography
													variant="caption"
													color="textSecondary"
												>
													Click to select range by
													coordinates
												</Typography>
											</Box>
										</Box>
									)}
									<TextField
										label={'Sheet (Name or ID)'}
										value={rangeValue.sheet || ''}
										onChange={(e) =>
											updateField('sheet', e.target.value)
										}
										fullWidth
										size="small"
										helperText={
											!rangeValue.sheet
												? 'First sheet will be selected'
												: ' '
										}
										FormHelperTextProps={{
											sx: {
												position: 'absolute',
												bottom: -20,
												mx: 0
											}
										}}
									/>
									<Box display="flex" gap={2}>
										<TextField
											label="Start Column"
											value={rangeValue.startColumn || ''}
											onChange={(e) =>
												updateField(
													'startColumn',
													e.target.value
												)
											}
											fullWidth
											size="small"
										/>
										<TextField
											label="Start Row"
											value={rangeValue.startRow || ''}
											onChange={(e) =>
												updateField(
													'startRow',
													e.target.value
												)
											}
											fullWidth
											size="small"
										/>
									</Box>
									<Box display="flex" gap={2}>
										<TextField
											label="End Column"
											value={rangeValue.endColumn || ''}
											onChange={(e) =>
												updateField(
													'endColumn',
													e.target.value
												)
											}
											fullWidth
											size="small"
										/>
										<TextField
											label="End Row"
											value={rangeValue.endRow || ''}
											onChange={(e) =>
												updateField(
													'endRow',
													e.target.value
												)
											}
											fullWidth
											size="small"
										/>
									</Box>
								</Box>

								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										variant="body2"
										color="textSecondary"
										fontWeight="bold"
									>
										OR
									</Typography>
								</Box>

								<Box
									flex={1}
									display="flex"
									flexDirection="column"
									gap={2}
									position="relative"
									border="1px solid #e2e8f0"
									borderRadius={2}
									p={2}
								>
									{rangeMode !== 'namedRange' && (
										<Box
											position="absolute"
											top={0}
											left={0}
											right={0}
											bottom={0}
											bgcolor="rgba(255, 255, 255, 0.4)"
											sx={{
												backdropFilter: 'blur(2px)',
												cursor: 'pointer',
												zIndex: 10,
												borderRadius: 2,
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												'& .hover-text': {
													opacity: 0,
													transition: 'opacity 0.2s'
												},
												'&:hover .hover-text': {
													opacity: 1
												}
											}}
											onClick={() =>
												setRangeMode('namedRange')
											}
										>
											<Box
												className="hover-text"
												display="flex"
												flexDirection="column"
												alignItems="center"
												sx={{
													background:
														'rgba(255,255,255,0.8)',
													padding: 1,
													borderRadius: 1
												}}
											>
												<Typography
													variant="subtitle2"
													fontWeight="bold"
												>
													Named Range
												</Typography>
												<Typography
													variant="caption"
													color="textSecondary"
												>
													Click to select by named
													range
												</Typography>
											</Box>
										</Box>
									)}
									<TextField
										label="Named Range"
										value={rangeValue.namedRange || ''}
										onChange={(e) =>
											updateField(
												'namedRange',
												e.target.value
											)
										}
										fullWidth
										size="small"
									/>
								</Box>
							</Box>
							{validationError && (
								<Typography
									color="error"
									variant="body2"
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1
									}}
								>
									<i className="fa-solid fa-triangle-exclamation" />
									{validationError}
								</Typography>
							)}
						</Box>
						{/* Mock table visualization */}
						<Box
							mt={2}
							p={2}
							border="1px solid #ddd"
							borderRadius={2}
						>
							{(() => {
								const startC =
									getColInt(rangeValue.startColumn) || 1;
								const endC =
									getColInt(rangeValue.endColumn) || Infinity;
								const startR =
									getRowInt(rangeValue.startRow) || 1;
								const endR =
									getRowInt(rangeValue.endRow) || Infinity;

								const nRows =
									endR === Infinity
										? '∞'
										: Math.max(0, endR - startR + 1);
								const nCols =
									endC === Infinity
										? '∞'
										: Math.max(0, endC - startC + 1);
								const nCells =
									endR === Infinity || endC === Infinity
										? '∞'
										: (nRows as number) * (nCols as number);

								const hasTopEllipsis = startR >= 3;
								const hasLeftEllipsis = startC >= 3;
								const firstVisibleC = hasLeftEllipsis
									? startC
									: 1;
								const lastVisibleC = firstVisibleC + 9;
								const hasRightEllipsis =
									endC !== Infinity && endC > lastVisibleC;
								const firstVisibleR = hasTopEllipsis
									? startR
									: 1;
								const lastVisibleR = firstVisibleR + 5;
								const hasBottomEllipsis =
									endR !== Infinity && endR > lastVisibleR;

								return (
									<>
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="center"
											mb={2}
										>
											<Typography
												variant="subtitle2"
												color="textSecondary"
											>
												Preview (max 10 cols x 6 rows)
											</Typography>
											<Typography
												variant="subtitle2"
												fontWeight="bold"
												color="textPrimary"
											>
												{nCells} cells selected. {nRows}{' '}
												rows. {nCols} columns.
											</Typography>
										</Box>
										<Box
											display="flex"
											flexDirection="column"
											alignItems="center"
											width="100%"
										>
											{hasTopEllipsis && (
												<Typography
													variant="caption"
													color="textSecondary"
												>
													... ({startR - 1} rows
													above)
												</Typography>
											)}
											<Box
												display="flex"
												width="100%"
												alignItems="center"
												justifyContent="center"
											>
												{hasLeftEllipsis && (
													<Typography
														variant="caption"
														color="textSecondary"
														sx={{
															writingMode:
																'vertical-rl',
															transform:
																'rotate(180deg)',
															mr: 1
														}}
													>
														... ({startC - 1} cols
														left)
													</Typography>
												)}
												<Box
													position="relative"
													width="100%"
												>
													{((rangeMode ===
														'namedRange' &&
														!!rangeValue.namedRange) ||
														(rangeMode ===
															'coordinates' &&
															startC === 1 &&
															startR === 1 &&
															endC === Infinity &&
															endR ===
																Infinity)) && (
														<Box
															position="absolute"
															top={0}
															left={0}
															right={0}
															bottom={0}
															bgcolor="rgba(255, 255, 255, 0.4)"
															sx={{
																backdropFilter:
																	'blur(2px)',
																zIndex: 10,
																display: 'flex',
																alignItems:
																	'center',
																justifyContent:
																	'center'
															}}
														>
															<Typography
																variant="h6"
																color="textPrimary"
																fontWeight="bold"
																sx={{
																	background:
																		'rgba(255, 255, 255, 0.8)',
																	padding:
																		'8px 16px',
																	borderRadius: 2
																}}
															>
																{rangeMode ===
																'namedRange'
																	? `Will select the preconfigured range of "${rangeValue.namedRange}"`
																	: 'Full sheet selected'}
															</Typography>
														</Box>
													)}
													<table
														style={{
															width: '100%',
															borderCollapse:
																'collapse',
															textAlign: 'center'
														}}
													>
														<tbody>
															{Array.from({
																length: 6
															}).map(
																(_, rIdx) => {
																	const currentRow =
																		firstVisibleR +
																		rIdx;
																	return (
																		<tr
																			key={
																				rIdx
																			}
																		>
																			{Array.from(
																				{
																					length: 10
																				}
																			).map(
																				(
																					_,
																					cIdx
																				) => {
																					const currentCol =
																						firstVisibleC +
																						cIdx;
																					let inRange =
																						!validationError &&
																						currentCol >=
																							startC &&
																						currentCol <=
																							endC &&
																						currentRow >=
																							startR &&
																						currentRow <=
																							endR;
																					if (
																						rangeMode ===
																							'namedRange' &&
																						!!rangeValue.namedRange
																					) {
																						inRange = true;
																					}
																					return (
																						<td
																							key={
																								cIdx
																							}
																							style={{
																								border: '1px solid #e2e8f0',
																								padding:
																									'8px',
																								backgroundColor:
																									inRange
																										? '#c7d2fe'
																										: '#f8fafc',
																								transition:
																									'background-color 0.2s',
																								height: '24px'
																							}}
																						/>
																					);
																				}
																			)}
																		</tr>
																	);
																}
															)}
														</tbody>
													</table>
												</Box>
												{hasRightEllipsis && (
													<Typography
														variant="caption"
														color="textSecondary"
														sx={{
															writingMode:
																'vertical-rl',
															transform:
																'rotate(180deg)',
															ml: 1
														}}
													>
														... (to col {endC})
													</Typography>
												)}
											</Box>
											{hasBottomEllipsis && (
												<Typography
													variant="caption"
													color="textSecondary"
													mt={1}
												>
													... (to row {endR})
												</Typography>
											)}
										</Box>
									</>
								);
							})()}
						</Box>
					</Box>
				)}
				{activeTab === 1 && (
					<Box height="100%" px={2} py={0}>
						<JsonEditorDialog
							value={props.value}
							valueOwner={props.valueOwner}
							project={props.project}
							onClose={props.onClose}
							embedded={true}
							onChange={(newVal) => setRangeValue(newVal)}
						/>
					</Box>
				)}
			</Box>

			<div
				style={{
					padding: '20px 40px',
					borderTop: '1px solid var(--border-color)',
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '12px'
				}}
			>
				<Button
					style={{
						background: 'transparent',
						color: 'var(--text-color)'
					}}
					onClick={(e: any) => props.onClose?.(e)}
				>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={!!validationError}>
					Save value
				</Button>
			</div>
		</div>
	);
};

export default SpreadsheetRangeBuilderDialog;
